/* F&E: Add to Cart behavior on product pages.
 *
 * All screen sizes:
 * - The button reads "Add to Cart" even before a size is picked.
 * - Pressing it with no size still adds nothing: the size-required guard
 *   in product-block-variant-selector.js scrolls to the sizes and shows
 *   "Please select a size". That guard stays the single safeguard.
 * - If the shopper pressed Add to Cart and was prompted for a size,
 *   their next size tap completes the add. A size tapped on its own
 *   only selects; pressing Add to Cart then adds it as usual.
 *
 * Phones only:
 * - A sticky Add to Cart button is pinned to the bottom of the screen
 *   whenever the page's own Add to Cart button is off screen. It hides
 *   once the real button scrolls into view and returns if the shopper
 *   scrolls past it. It only ever clicks the page's real button.
 *
 * Loaded from snippets/product-block-buy-buttons.liquid. Safe to remove:
 * delete that one script line and this file.
 */
(() => {
  if (window.__feStickyAtcInit) {
    return
  }
  window.__feStickyAtcInit = true;

  if (window.Shopify && window.Shopify.designMode) {
    return
  }

  const MOBILE_MQ = "(max-width: 719.9px)";
  const VARIANT_CHANGED_EVENT = "product:variant:changed";
  const PENDING_ATTR = "data-fe-size-pending";
  const ON_CLASS = "fe-sticky-atc-on";
  const HEADER_OFFSET = 70;
  const INTENT_TTL_MS = 120000;

  const mq = window.matchMedia(MOBILE_MQ);
  const html = document.documentElement;
  const container = document.querySelector("main-product-default");
  if (!container) {
    return
  }

  const realButton = () => container.querySelector("[js-add-to-cart-button]");
  if (!realButton()) {
    return
  }

  const isPending = () => html.hasAttribute(PENDING_ATTR);

  // --- Styles -------------------------------------------------------------
  const style = document.createElement("style");
  style.textContent = `
    #fe-sticky-atc { display: none; }
    @media ${MOBILE_MQ} {
      #fe-sticky-atc {
        display: block;
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 10;
        padding: 12px 16px calc(12px + env(safe-area-inset-bottom, 0px));
        background: #fff;
        border-top: 1px solid #afb6c0;
        box-shadow: 0 -6px 16px rgba(0, 0, 0, 0.08);
        transform: translateY(110%);
        visibility: hidden;
        transition: transform 0.25s ease, visibility 0s linear 0.25s;
      }
      html.${ON_CLASS} #fe-sticky-atc {
        transform: none;
        visibility: visible;
        transition: transform 0.25s ease;
      }
      .fe-sticky-atc__btn {
        width: 100%;
        min-height: 56px;
        margin: 0;
        padding: 6px 12px;
        border: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 1px;
        cursor: pointer;
        -webkit-appearance: none;
        appearance: none;
      }
      .fe-sticky-atc__btn[disabled] {
        background: rgba(21, 42, 71, 0.1) !important;
        color: #152a47 !important;
        cursor: default;
      }
      .fe-sticky-atc__size {
        font-size: 13px;
        line-height: 16px;
      }
      .fe-sticky-atc__size:empty { display: none; }
      html.fe-sticky-atc-ready body { padding-bottom: 96px; }
      html.${ON_CLASS} #gorgias-chat-container iframe,
      html.${ON_CLASS} iframe[class*="gorgias-chat"] {
        bottom: calc(96px + env(safe-area-inset-bottom, 0px)) !important;
      }
    }
  `;
  document.head.appendChild(style);

  // --- Sticky bar (shown on phones only, via the media query above) -------
  const bar = document.createElement("div");
  bar.id = "fe-sticky-atc";

  const stickyButton = document.createElement("button");
  stickyButton.type = "button";
  stickyButton.className = "fe-sticky-atc__btn";

  const labelEl = document.createElement("span");
  labelEl.className = "fe-sticky-atc__label";

  const sizeEl = document.createElement("span");
  sizeEl.className = "fe-sticky-atc__size";

  stickyButton.appendChild(labelEl);
  stickyButton.appendChild(sizeEl);
  bar.appendChild(stickyButton);
  document.body.appendChild(bar);
  html.classList.add("fe-sticky-atc-ready");

  let stylesCopied = false;

  // Match the theme's own button look (copied once, from an enabled button)
  function copyStyles(real) {
    if (stylesCopied || real.disabled) {
      return
    }
    const cs = window.getComputedStyle(real);
    if (!cs.fontFamily) {
      return
    }
    stickyButton.style.fontFamily = cs.fontFamily;
    stickyButton.style.fontSize = cs.fontSize;
    stickyButton.style.fontWeight = cs.fontWeight;
    stickyButton.style.lineHeight = "22px";
    stickyButton.style.letterSpacing = cs.letterSpacing;
    stickyButton.style.textTransform = cs.textTransform;
    stickyButton.style.borderRadius = cs.borderRadius;
    stickyButton.style.color = cs.color;
    stickyButton.style.backgroundColor = cs.backgroundColor;
    stickyButton.style.backgroundImage = cs.backgroundImage;
    sizeEl.style.fontFamily = window.getComputedStyle(document.body).fontFamily;
    stylesCopied = true;
  }

  // Keep "Add to Cart" on the button while no size is picked (all screens)
  function restoreLabels() {
    if (!isPending()) {
      return
    }
    container
      .querySelectorAll("[js-add-to-cart-button] [data-fe-original]")
      .forEach((el) => {
        const original = el.getAttribute("data-fe-original");
        if (original !== null && el.textContent !== original) {
          el.textContent = original;
        }
      });
  }

  function selectedSizeText() {
    if (isPending()) {
      return ""
    }
    const selector = container.querySelector("product-block-variant-selector");
    if (!selector) {
      return ""
    }
    const input = selector.querySelector('input[type="radio"][js-variant-input]:checked');
    if (!input || !input.parentElement) {
      return ""
    }
    const chip = input.parentElement.querySelector("[js-chip]");
    const value = chip ? (chip.getAttribute("aria-label") || "").trim() : "";
    if (!value) {
      return ""
    }
    const group = input.closest("[js-variant-group-container]");
    const name = group
      ? (group.querySelector(".label-text")?.textContent || "").replace(":", "").trim()
      : "";
    return name ? `${name} ${value}` : value;
  }

  function sync() {
    restoreLabels();

    const real = realButton();
    if (!real || !mq.matches) {
      html.classList.remove(ON_CLASS);
      return
    }

    copyStyles(real);

    const source = real.querySelector(".label-default") || real;
    const text = (source.textContent || "").trim();
    if (labelEl.textContent !== text) {
      labelEl.textContent = text;
    }

    const sizeText = selectedSizeText();
    if (sizeEl.textContent !== sizeText) {
      sizeEl.textContent = sizeText;
    }

    stickyButton.disabled = real.disabled;

    const rect = real.getBoundingClientRect();
    const show =
      rect.width > 0 &&
      (rect.top >= window.innerHeight - 20 || rect.bottom <= HEADER_OFFSET);
    html.classList.toggle(ON_CLASS, show);
  }

  // Run on the next frame, with a timer as backup
  let queued = false;
  function schedule() {
    if (queued) {
      return
    }
    queued = true;
    const run = () => {
      if (!queued) {
        return
      }
      queued = false;
      sync();
    };
    window.requestAnimationFrame(run);
    window.setTimeout(run, 150);
  }

  function scheduleSoon() {
    schedule();
    window.setTimeout(schedule, 400);
  }

  window.addEventListener("scroll", schedule, { passive: true });
  window.addEventListener("resize", schedule);
  // The size-required guard re-applies its label on pageshow; undo it at once
  window.addEventListener("pageshow", () => {
    restoreLabels();
    window.setTimeout(schedule, 0);
  });
  if (mq.addEventListener) {
    mq.addEventListener("change", schedule);
  }
  document.addEventListener("change", scheduleSoon, true);
  document.addEventListener(VARIANT_CHANGED_EVENT, scheduleSoon, true);

  // The theme repaints the buy buttons on variant change; keep in step
  new MutationObserver(schedule).observe(container, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ["disabled"],
  });
  new MutationObserver(schedule).observe(html, {
    attributes: true,
    attributeFilter: [PENDING_ATTR],
  });

  // --- Sticky tap: always goes through the page's real button -------------
  stickyButton.addEventListener("click", () => {
    const real = realButton();
    if (real && !real.disabled) {
      real.click();
    }
  });

  // --- Size tap completes an add the shopper already asked for ------------
  let intentUntil = 0;

  // Runs before the size-required guard (window is first in capture order)
  window.addEventListener(
    "submit",
    (event) => {
      if (!isPending()) {
        return
      }
      const form = event.target;
      if (
        form &&
        form.matches &&
        form.matches("[data-product-form]") &&
        container.contains(form)
      ) {
        intentUntil = Date.now() + INTENT_TTL_MS;
      }
    },
    true,
  );

  function waitForVariantChange() {
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (!done) {
          done = true;
          resolve();
        }
      };
      document.addEventListener(VARIANT_CHANGED_EVENT, finish, {
        once: true,
        capture: true,
      });
      window.setTimeout(finish, 700);
    });
  }

  // Only add once the form really holds the size the shopper tapped
  function addWhenFormMatches(variantId, attempt) {
    const hidden = container.querySelector(
      '[data-product-form] input[type="hidden"][name="id"]',
    );
    const checked = container.querySelector(
      'product-block-variant-selector input[type="radio"][js-variant-input]:checked',
    );
    const real = realButton();

    if (
      hidden &&
      hidden.value === variantId &&
      checked &&
      checked.value === variantId &&
      !isPending()
    ) {
      if (real && !real.disabled) {
        real.click();
      }
      return
    }

    if (attempt < 40) {
      window.setTimeout(() => addWhenFormMatches(variantId, attempt + 1), 100);
    }
  }

  document.addEventListener(
    "change",
    (event) => {
      if (!intentUntil) {
        return
      }
      const input = event.target;
      if (
        !input ||
        !input.matches ||
        !input.matches('input[type="radio"][js-variant-input]') ||
        !container.contains(input)
      ) {
        return
      }

      const fresh = Date.now() < intentUntil;
      intentUntil = 0;
      if (!fresh) {
        return
      }

      const variantId = input.value;
      waitForVariantChange().then(() => {
        window.setTimeout(() => addWhenFormMatches(variantId, 0), 100);
      });
    },
    true,
  );

  window.setTimeout(schedule, 300);
  sync();
})();
